import type {PerkCard as PerkCardType} from '@cohort/components-xps/components/cards/PerkCard';
import PerkCard from '@cohort/components-xps/components/cards/PerkCard';
import ExpandableDesc from '@cohort/components-xps/components/ExpandableDesc';
import GradientToBottom from '@cohort/components-xps/components/GradientToBottom';
import {classNames} from '@cohort/shared-frontend/utils/classNames';
import {MobileNavTabs} from '@cohort/wallet/components/MobileNavTabs';
import {MobileSection} from '@cohort/wallet/components/MobileSection';
import {useUserStore} from '@cohort/wallet/hooks/stores/user';
import useThemeContext from '@cohort/wallet/hooks/useThemeContext';
import type {SectionName} from '@cohort/wallet/lib/Utils';
import type {DigitalAssetTransferInvitationWDto} from '@cohort/wallet-schemas/ownership';
import React, {useCallback, useMemo, useRef, useState} from 'react';
import {useInView} from 'react-intersection-observer';

type DigitalAssetInvitationContentMobileProps = {
  nftTransferInvitation: DigitalAssetTransferInvitationWDto;
  button: JSX.Element;
};

export const DigitalAssetInvitationContentMobile: React.FC<
  DigitalAssetInvitationContentMobileProps
> = ({nftTransferInvitation, button}) => {
  const [visibleSections, setVisibleSections] = useState<
    Partial<Record<SectionName, boolean | undefined>>
  >({});
  const {backgroundColor} = useThemeContext();
  const {ref: navTabsRef, inView: navTabsInView} = useInView({threshold: 1});
  const {accentColor, hasDarkBg} = useThemeContext();
  const stickyBottomBuyButtonRef = useRef<HTMLDivElement | null>(null);
  const user = useUserStore(store => store.user);

  const sectionsNames: SectionName[] = useMemo(() => {
    return [
      ...(nftTransferInvitation.collection.description.length > 0
        ? ['description' as SectionName]
        : []),
      ...(nftTransferInvitation.perks.length > 0 ? ['perks' as SectionName] : []),
    ];
  }, [nftTransferInvitation]);

  const handleVisibilityChange = useCallback((sectionName: SectionName, isVisible: boolean) => {
    setVisibleSections(prev => ({...prev, [sectionName]: isVisible}));
  }, []);

  const visibleSection = useMemo(() => {
    for (const sectionName of sectionsNames) {
      if (visibleSections[sectionName] === true) {
        return sectionName;
      }
    }
    return undefined;
  }, [sectionsNames, visibleSections]);

  const stickyBottomBuyButtonHeight = stickyBottomBuyButtonRef.current?.clientHeight ?? 0;

  return (
    <div className="-mx-4 -mt-4" style={{paddingBottom: `${stickyBottomBuyButtonHeight}px`}}>
      <div className="w-full">
        <div className="sticky top-[-1rem] z-50">
          <MobileNavTabs
            visibleSection={visibleSection}
            sectionsNames={sectionsNames}
            tracking={{
              namespace: 'digitalAssets.transfer.tabs',
            }}
          />
        </div>
        <div ref={navTabsRef} />
        <div className={classNames(sectionsNames.length > 0 && 'mt-5')}>
          {nftTransferInvitation.collection.description.length > 0 && (
            <MobileSection name="description" onVisibilityChange={handleVisibilityChange}>
              <ExpandableDesc
                hasDarkBg={hasDarkBg}
                backgroundColor={backgroundColor}
                description={nftTransferInvitation.collection.description}
              />
            </MobileSection>
          )}
          {nftTransferInvitation.perks.length > 0 && (
            <MobileSection name="perks" onVisibilityChange={handleVisibilityChange}>
              <div className="space-y-2">
                {nftTransferInvitation.perks.map(perk => {
                  const perkCard: PerkCardType = {
                    perkVideoFileKey: perk.videoFileKey ?? null,
                    perkThumbnailFileKey: perk.thumbnailFileKey ?? null,
                    perkDisplayName: perk.displayName,
                    canBeUsed: undefined,
                    digitalAssetImageFileKey: null,
                    perkBannerFileKey: perk.bannerFileKey,
                    hasDarkBg,
                    accentColor,
                    raffleStatus: undefined,
                    statusMessage: undefined,
                  };

                  perkCard.hideMediaCover =
                    perkCard.perkBannerFileKey === null && perkCard.perkThumbnailFileKey === null;

                  return <PerkCard key={perk.id} perkCard={perkCard} />;
                })}
              </div>
            </MobileSection>
          )}
          <div
            ref={stickyBottomBuyButtonRef}
            className={classNames(
              'fixed left-0 z-[2] w-full items-center transition duration-500 ease-in-out md:hidden',
              navTabsInView ? 'translate-y-full opacity-0' : 'opacity-100',
              user ? 'bottom-20' : 'bottom-0'
            )}
          >
            <GradientToBottom backgroundColor={backgroundColor} />
            <div className="p-5" style={{backgroundColor}}>
              {button}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
